import { DateRangeType } from '../types';

export const isInvalidDate = (value: unknown) =>
  !value || (value as Date).toString() === 'Invalid Date';

export const normalizeDate = (value: Date | null | undefined) =>
  value ? new Date(value.getFullYear(), value.getMonth(), value.getDate()) : null;

export const sanitizePeriodNumber = (value: string) => value.replaceAll(/\D/g, '').slice(0, 2);

type DateRangeValidationInput = {
  dateRangeType: DateRangeType;
  from: Date | null | undefined;
  to: Date | null | undefined;
  periodNumber: string;
};

type DateRangeValidationResult = {
  periodNumberError: boolean;
  fromInvalid: boolean;
  toInvalid: boolean;
  orderInvalid: boolean;
  fromError: boolean;
  toError: boolean;
  fromErrorMessage: string;
  toErrorMessage: string;
  normalizedFrom: Date | null;
  normalizedTo: Date | null;
  /** Single source of truth for "does this date range block export", shared by every field/footer that gates on it. */
  hasError: boolean;
};

export const getDateRangeValidation = ({
  dateRangeType,
  from,
  to,
  periodNumber
}: DateRangeValidationInput): DateRangeValidationResult => {
  const periodNumberError =
    dateRangeType === DateRangeType.RANGE &&
    (!periodNumber || periodNumber.length < 1 || /^0{1,2}$/.test(periodNumber));

  const fromInvalid = dateRangeType === DateRangeType.DATES && isInvalidDate(from);
  const toInvalid = dateRangeType === DateRangeType.DATES && isInvalidDate(to);

  const normalizedFrom = normalizeDate(from);
  const normalizedTo = normalizeDate(to);

  const hasBothDates =
    dateRangeType === DateRangeType.DATES &&
    !!from &&
    !!to &&
    !fromInvalid &&
    !toInvalid &&
    !!normalizedFrom &&
    !!normalizedTo;

  const orderInvalid = hasBothDates && normalizedFrom.getTime() > normalizedTo.getTime();

  const fromError = dateRangeType === DateRangeType.DATES && (fromInvalid || orderInvalid);
  const toError = dateRangeType === DateRangeType.DATES && (toInvalid || orderInvalid);

  const getFromErrorMessage = (): string => {
    if (!fromError) return '';
    if (isInvalidDate(from)) return 'Please select a valid start date';
    return 'Start date cannot be after end date';
  };

  const getToErrorMessage = (): string => {
    if (!toError) return '';
    if (isInvalidDate(to)) return 'Please select a valid end date';
    return 'End date cannot be before start date';
  };

  const fromErrorMessage = getFromErrorMessage();
  const toErrorMessage = getToErrorMessage();

  return {
    periodNumberError,
    fromInvalid,
    toInvalid,
    orderInvalid,
    fromError,
    toError,
    fromErrorMessage,
    toErrorMessage,
    normalizedFrom,
    normalizedTo,
    hasError: periodNumberError || fromError || toError
  };
};
